import { reactive } from 'vue'

export const chatState = reactive({
  unreadCountsByParticipant: {},
  unreadCountsByRef: {},
  unreadCountsByConvId: {},
  customersUnreadCount: 0,
  customersUnreadMessagesCount: 0,
  ridersUnreadCount: 0,
  ridersUnreadMessagesCount: 0,
  totalUnreadCount: 0,
  conversations: [],
  isInitialized: false,
})

let listenersRegistered = false

export function updateChatStateFromConversations(conversations = []) {
  window.chatState = chatState
  const currentUserId = window.XenoTalk?.userId || 'admin-1'
  const unreadByParticipant = {}
  const unreadByRef = {}
  const unreadByConvId = {}

  let customersUnreadConvs = 0
  let customersUnreadMsgs = 0
  let ridersUnreadConvs = 0
  let ridersUnreadMsgs = 0
  let totalUnreadConvs = 0

  conversations.forEach((c) => {
    if (!c) return
    const convId = c._id || c.id
    const refId = c.referenceId

    const isP1 = c.participant1Id === currentUserId
    const otherId = isP1 ? c.participant2Id : c.participant1Id
    const otherRole = isP1 ? c.participant2Role : c.participant1Role

    let unread = isP1
      ? Number(c.participant1UnreadCount || 0)
      : Number(c.participant2UnreadCount || 0)

    if (unread === 0 && c.hasUnread && c.lastSenderId !== currentUserId) {
      unread = 1
    }

    if (convId) unreadByConvId[convId] = unread
    if (refId) unreadByRef[refId] = unread
    if (otherId) unreadByParticipant[String(otherId)] = unread

    // Extraire l'ID depuis la referenceId pour couvrir les cas
    // où participant2Id (userId) != riderId métier affiché dans le tableau.
    // ex: referenceId = 'v3-admin-rider-abc123' → indexer 'abc123' aussi
    if (refId) {
      let extractedId = null
      if (refId.startsWith('v3-admin-rider-')) {
        extractedId = refId.replace('v3-admin-rider-', '')
      } else if (refId.startsWith('v3-admin-customer-')) {
        extractedId = refId.replace('v3-admin-customer-', '')
      }
      if (extractedId && extractedId !== String(otherId)) {
        unreadByParticipant[extractedId] = unread
      }
    }

    const isCustomer =
      otherRole === 'client' ||
      otherRole === 'customer' ||
      (refId && refId.startsWith('v3-admin-customer-')) ||
      c.referenceType === 'customer'

    const isRider =
      otherRole === 'rider' ||
      (refId && refId.startsWith('v3-admin-rider-')) ||
      c.referenceType === 'rider'

    if (unread > 0) {
      totalUnreadConvs += 1
      if (isCustomer) {
        customersUnreadConvs += 1
        customersUnreadMsgs += unread
      } else if (isRider) {
        ridersUnreadConvs += 1
        ridersUnreadMsgs += unread
      }
    }
  })

  chatState.unreadCountsByParticipant = unreadByParticipant
  chatState.unreadCountsByRef = unreadByRef
  chatState.unreadCountsByConvId = unreadByConvId
  chatState.customersUnreadCount = customersUnreadConvs
  chatState.customersUnreadMessagesCount = customersUnreadMsgs
  chatState.ridersUnreadCount = ridersUnreadConvs
  chatState.ridersUnreadMessagesCount = ridersUnreadMsgs
  chatState.totalUnreadCount = totalUnreadConvs
  chatState.conversations = conversations
  chatState.isInitialized = true
}

export function initChatStateListeners() {
  if (listenersRegistered || typeof window === 'undefined') return
  listenersRegistered = true

  const handleUpdate = (e) => {
    const detail = e?.detail || {}
    const convs = detail.conversations || window.XenoTalk?.conversations || []
    updateChatStateFromConversations(convs)
  }

  window.addEventListener('xenotalk:conversations_updated', handleUpdate)
  window.addEventListener('xenotalk:unread_change', handleUpdate)
  window.addEventListener('xenotalk:message_received', () => {
    if (window.XenoTalk?.fetchConversations) {
      window.XenoTalk.fetchConversations(true)
    }
  })
  window.addEventListener('xenotalk:message_read', handleUpdate)
  window.addEventListener('xenotalk:ready', () => {
    if (window.XenoTalk?.conversations) {
      updateChatStateFromConversations(window.XenoTalk.conversations)
    }
  })

  // Synchronisation immédiate si XenoTalk a déjà des conversations
  if (window.XenoTalk?.conversations?.length > 0) {
    updateChatStateFromConversations(window.XenoTalk.conversations)
  }
}

/**
 * Obtenir le nombre de messages non lus pour un client donné
 */
export function getClientUnreadCount(clientId) {
  if (!clientId) return 0
  const idStr = String(clientId)
  if (chatState.unreadCountsByParticipant[idStr] !== undefined) {
    return chatState.unreadCountsByParticipant[idStr]
  }
  const refKey = `v3-admin-customer-${idStr}`
  if (chatState.unreadCountsByRef[refKey] !== undefined) {
    return chatState.unreadCountsByRef[refKey]
  }
  if (window.XenoTalk?.getConversationUnreadCount) {
    return window.XenoTalk.getConversationUnreadCount({ id: idStr, referenceId: refKey })
  }
  return 0
}

/**
 * Obtenir le nombre de messages non lus pour un livreur donné.
 * On cherche par toutes les combinaisons possibles :
 *  - userId du rider (el.id)
 *  - riderId métier (el.riderId)
 *  - referenceId v3-admin-rider-${userId} ou v3-admin-rider-${riderId}
 *  - Appel direct au widget XenoTalk en dernier recours
 * @param {Object|string} riderOrId - objet rider complet ou un ID quelconque
 */
export function getRiderUnreadCount(riderOrId) {
console.log("🔍 ~ getRiderUnreadCount ~ v3/rapidoo-dashboard-v3/src/features/chat/chatState.js:159 ~ riderOrId:", riderOrId);
  if (!riderOrId) return 0

  // Extraire tous les identifiants possibles
  const candidates = []
  if (typeof riderOrId === 'object') {
    const userId = riderOrId.userId 
    const rId   = riderOrId.riderId || riderOrId.metadata?.riderId
    if (userId) candidates.push(String(userId))
      if (rId && String(rId) !== String(userId)) candidates.push(String(rId))
      } else {
    candidates.push(String(riderOrId))
  }
  
  console.log("🔍 ~ getRiderUnreadCount ~ v3/rapidoo-dashboard-v3/src/features/chat/chatState.js:164 ~ candidates:", candidates);
  // 1. Chercher dans le cache par participant ID
  for (const id of candidates) {
    const v = chatState.unreadCountsByParticipant[id]
    if (v !== undefined && v > 0) return v
  }

  // 2. Chercher dans le cache par referenceId
  for (const id of candidates) {
    const refKey = `v3-admin-rider-${id}`
    const v = chatState.unreadCountsByRef[refKey]
    if (v !== undefined && v > 0) return v
  }

  // 3. Chercher via le widget XenoTalk en dernier recours
  if (window.XenoTalk?.getConversationUnreadCount) {
    for (const id of candidates) {
      const refKey = `v3-admin-rider-${id}`
      const count = window.XenoTalk.getConversationUnreadCount({ id, referenceId: refKey })
      if (count > 0) return count
    }
  }

  return 0
}
